import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtMoney, formatApiError } from "../lib/api";

/**
 * Shared dialog for SWIFT bank transfers and USDT crypto wires.
 * Backend creates a manual_orders record with a reference; admin confirms receipt
 * to credit UCS to the buyer's wallet.
 */
export default function ManualPaymentDialog({ open, onClose, method, projectId, projectName, tons, currency, onSuccess }) {
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) { setOrder(null); setErr(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/payments/manual-order", {
          project_id: projectId, tons: Number(tons), currency, method,
        });
        if (!cancelled) {
          setOrder(data);
          onSuccess?.(data);
        }
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e?.response?.data?.detail) || e.message || "Could not create order.");
      }
    })();
    return () => { cancelled = true; };
  }, [open, method, projectId, tons, currency, onSuccess]);

  if (!open) return null;

  const isSwift = method === "swift";
  const title = isSwift ? "SWIFT bank transfer" : "USDT (stablecoin) wire";
  const ins = order?.instructions;

  const copy = (txt, label) => {
    navigator.clipboard?.writeText(String(txt));
    toast.success(`${label} copied`);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface border border-line w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`${method}-dialog`}
      >
        <div className="border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag">{title}</div>
            <div className="font-serif text-xl mt-1">{projectName || "UCS purchase"}</div>
            <div className="text-xs font-mono text-ink2 mt-1">
              {Number(tons).toFixed(0)} tCO₂ · {currency}
            </div>
          </div>
          <button onClick={onClose} className="text-ink2 hover:text-ink text-2xl leading-none" data-testid={`${method}-close`} aria-label="Close">×</button>
        </div>

        <div className="p-6 space-y-4">
          {err && <div className="text-sm text-danger border border-danger/40 bg-danger/5 px-3 py-2" data-testid={`${method}-err`}>{err}</div>}
          {!order && !err && <div className="text-sm text-ink2 font-mono">Generating order reference…</div>}

          {order && ins && (
            <>
              <div className="border border-ink p-4 bg-muted/20">
                <div className="label-tag">Order reference (include in wire memo)</div>
                <div className="flex items-center justify-between gap-3 mt-2">
                  <div className="mono-num text-2xl" data-testid={`${method}-ref`}>{order.ref}</div>
                  <button onClick={() => copy(order.ref, "Reference")} className="text-xs font-mono uppercase tracking-wider underline" data-testid={`${method}-copy-ref`}>copy</button>
                </div>
              </div>

              <div className="space-y-1 border border-line divide-y divide-line">
                {isSwift ? (
                  <>
                    <Row k="Beneficiary" v={ins.beneficiary} onCopy={() => copy(ins.beneficiary, "Beneficiary")} />
                    <Row k="Bank" v={ins.bank_name} onCopy={() => copy(ins.bank_name, "Bank name")} />
                    <Row k="BIC / SWIFT" v={ins.bic_swift} mono onCopy={() => copy(ins.bic_swift, "BIC")} />
                    <Row k="IBAN / Account" v={ins.iban_account} mono onCopy={() => copy(ins.iban_account, "IBAN")} />
                    <Row k="Bank address" v={ins.bank_address} small />
                    <Row k="Settlement currency" v={ins.settlement_currency} mono />
                    <Row
                      k="Amount to wire"
                      v={fmtMoney(ins.amount_to_wire, ins.settlement_currency)}
                      strong
                      onCopy={() => copy(ins.amount_to_wire, "Amount")}
                    />
                  </>
                ) : (
                  <>
                    <Row k="Network" v={ins.network} mono onCopy={() => copy(ins.network, "Network")} />
                    <Row k="Wallet address" v={ins.wallet_address} mono small onCopy={() => copy(ins.wallet_address, "Address")} />
                    <Row k="Amount (USDT)" v={`${ins.amount_to_send_usdt} USDT`} strong onCopy={() => copy(ins.amount_to_send_usdt, "Amount")} />
                    <div className="bg-surface p-3 text-[11px] text-ink2 font-mono">{ins.note}</div>
                  </>
                )}
              </div>

              <div className="text-xs text-ink2 font-mono border border-line bg-muted/30 p-3 leading-relaxed">
                Funds are credited to your UCS holdings once GreenBelt&apos;s compliance desk confirms receipt — typically <strong>1 business day</strong> for SWIFT, <strong>under 1 hour</strong> for USDT (post-confirmations). You can track the order status in your portfolio.
              </div>

              <div className="flex items-center justify-between gap-3 pt-2">
                <button type="button" onClick={onClose} className="text-xs font-mono uppercase tracking-wider text-ink2 hover:text-ink underline underline-offset-4" data-testid={`${method}-cancel`}>Close</button>
                <a
                  href={`mailto:?subject=GreenBelt order ${order.ref}&body=${encodeURIComponent(`Reference: ${order.ref}\nAmount: ${fmtMoney(order.total_usd, "USD")} USD\nMethod: ${method.toUpperCase()}\n\nPlease quote the reference in the wire memo.`)}`}
                  className="btn-outline !px-5 !py-2.5 !text-xs"
                  data-testid={`${method}-email-self`}
                >
                  Email these details
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, mono, small, strong, onCopy }) {
  return (
    <div className="flex items-center justify-between gap-4 bg-surface px-4 py-3">
      <div className="min-w-0">
        <div className="label-tag">{k}</div>
        <div className={`mt-1 ${mono ? "font-mono" : ""} ${small ? "text-xs" : "text-sm"} ${strong ? "font-serif text-base" : ""} text-ink break-all`}>{v || "—"}</div>
      </div>
      {onCopy && (
        <button onClick={onCopy} className="text-[10px] font-mono uppercase tracking-wider text-ink2 hover:text-ink underline underline-offset-4 shrink-0">copy</button>
      )}
    </div>
  );
}
